import React, { FC } from 'react';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Transaction } from '~/types';
import c from '~/utils/constants';
import { AmountField, FxRateField, TextField, TransactionResult } from '~/components';
import { useService } from '~/hooks/services';
import { getServiceParameters } from '~/utils/getServiceParameters';
import { isDisplayedEntryCategory } from '~/utils/transaction';

interface Props {
  transfer: Transaction;
  className?: string;
  onClose: () => void;
}

const SuccessScreen: FC<Props> = (props) => {
  const { transfer, className, onClose } = props;
  const [t] = useTranslation('payouts');
  const tt = (key: string): string => t(`transactions:${key}`);

  const parameters = transfer.details?.service?.parameters;
  const exchangeRate = transfer?.details?.currencyConversion?.sender?.rate;

  const serviceId = transfer.details?.service?.id;
  const { data: service } = useService(serviceId);

  return (
    <div className={className}>
      <TransactionResult transaction={transfer} onClose={onClose} />
      <div className="SuccessScreen-Result">
        <ResultCell>
          <TextField label={t('fromAccount')}>{transfer.sender.accountNumber}</TextField>
        </ResultCell>
        {parameters && (
          <ResultCell>
            <Space direction="vertical" size={16}>
              {getServiceParameters(transfer, service)?.map((param) => {
                return (
                  param && (
                    <TextField key={param.name} label={param.name}>
                      {param.value}
                    </TextField>
                  )
                );
              })}
            </Space>
          </ResultCell>
        )}
        <ResultCell>
          <Space direction="vertical" size={16}>
            <TextField label={tt('baseAmount')}>
              <AmountField amount={transfer.baseAmount} abs />
            </TextField>
            {exchangeRate && (
              <>
                <TextField label={tt('exchangeRate')}>
                  <FxRateField
                    baseCurrency={transfer?.baseAmount?.currency as string}
                    quoteCurrency={transfer?.totalAmount?.currency as string}
                    rate={exchangeRate}
                  />
                </TextField>
                <TextField label={t('convertedAmount')}>
                  <AmountField amount={transfer.amount} abs />
                </TextField>
              </>
            )}
            {transfer?.entries
              ?.filter((item) => isDisplayedEntryCategory(item.category))
              ?.map((item, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TextField key={index} label={item.description || tt(item.category)}>
                    <AmountField amount={item.amount} abs />
                  </TextField>
                );
              })}
            <TextField label={tt('totalAmount')}>
              <AmountField className="SendMoney-TotalAmount" amount={transfer.totalAmount} abs />
            </TextField>
          </Space>
        </ResultCell>
      </div>
    </div>
  );
};

const ResultCell: FC<any> = (props) => {
  const { children } = props;
  return <div className="SuccessScreen-ResultCell">{children}</div>;
};

export default styled(SuccessScreen)`
  .SuccessScreen-Result {
    border-radius: ${c.borderRadiusBase}px;
    overflow: hidden;
    border: 1px solid #f0f0f0;
    .SuccessScreen-ResultCell {
      padding: 16px 20px;
      background-color: #ffffff;
      border-bottom: 1px solid #f0f0f0;
      &:last-child {
        border-bottom: none;
      }
    }
  }
`;
